import type { Root } from 'mdast';
import type { Plugin } from 'unified';

interface NodeLike {
  type: string;
}

interface ParentLike extends NodeLike {
  children: NodeLike[];
}

function isParent(node: NodeLike): node is ParentLike {
  return 'children' in node && Array.isArray((node as ParentLike).children);
}

/**
 * Returns true if the node should be kept, false if it should be removed.
 * - Removes `image` nodes.
 * - Recursively strips images from children of parent nodes.
 * - Removes paragraphs that are left empty after stripping (i.e. image-only paragraphs).
 */
function keepNode(node: NodeLike): boolean {
  if (node.type === 'image') return false;
  if (isParent(node)) {
    node.children = node.children.filter(keepNode);
    if (node.type === 'paragraph' && node.children.length === 0) {
      return false;
    }
  }
  return true;
}

/** @internal Remark transformer: strips image nodes from the AST. */
function stripImages(tree: Root): void {
  // Root always has children; safe to treat as ParentLike for traversal.
  const root = tree as unknown as ParentLike;
  root.children = root.children.filter(keepNode);
}

/**
 * Remark plugin that removes all markdown images from the AST.
 * Useful for generating text-only previews of message content.
 * Image-only paragraphs are removed entirely to avoid blank lines.
 */
export function remarkStripImages(): ReturnType<Plugin<[], Root>> {
  return stripImages;
}
